/**
 * Client ↔ rago-web broker DTOs (ARCHITECTURE §4a slice) — the zod
 * validators behind contracts/openapi/web-broker.v1.yaml. The SPA types its
 * resources handling from these instead of hand-written interfaces.
 */
import { z } from "zod";

// Local copy of the list-envelope meta (importing index.js here would be a
// circular module evaluation once index re-exports this file).
const metaSchema = z.object({
  offset: z.number().int().min(0),
  limit: z.number().int().min(0),
  total: z.number().int().min(0),
});

export const claimTokenResponseSchema = z.object({
  claimToken: z.string(),
  expiresAt: z.iso.datetime(),
});

export const connectionCandidateSchema = z.object({
  uri: z.string(),
  // http exists only on owner-configured custom connection URLs; every
  // broker-synthesized candidate stays https.
  protocol: z.enum(["https", "http"]),
  address: z.string(),
  port: z.number().int(),
  local: z.boolean(),
  relay: z.boolean(),
  directHostname: z.string().optional(),
});
export type ConnectionCandidate = z.infer<typeof connectionCandidateSchema>;

export const serverScopedTokenSchema = z.object({
  token: z.string(),
  expiresAt: z.iso.datetime(),
});

/**
 * Outside-in reachability verdict (ARCHITECTURE §6.2 "the broker actively
 * probes published candidates"): the broker dials the server's public
 * candidate from the cloud, so "fully accessible outside your network" is a
 * statement about the internet, not about whichever browser is looking.
 * `directOk` is null until the first probe; `error` explains a false.
 */
export const reachabilityErrorSchema = z.enum([
  "no_public_candidate",
  "timeout",
  "refused",
  "tls",
  "wrong_server",
  "http_error",
  "unreachable",
]);
export type ReachabilityError = z.infer<typeof reachabilityErrorSchema>;

export const reachabilitySchema = z.object({
  directOk: z.boolean().nullable(),
  checkedAt: z.iso.datetime().nullable(),
  error: reachabilityErrorSchema.optional(),
  /** The exact URI the broker dialed — owner-facing surfaces only. */
  probedUri: z.string().optional(),
});
export type Reachability = z.infer<typeof reachabilitySchema>;

/** POST /v1/servers/{id}/reachability — the owner's on-demand probe. */
export const reachabilityProbeResponseSchema = reachabilitySchema.extend({
  publicAddress: z.string().nullable(),
  publicPort: z.number().int().nullable(),
});
export type ReachabilityProbeResponse = z.infer<typeof reachabilityProbeResponseSchema>;

export const resourceItemSchema = z.object({
  serverId: z.uuid(),
  name: z.string(),
  serverHash: z.string(),
  version: z.string().nullable(),
  latestVersion: z.string().optional(),
  owned: z.boolean(),
  ownerUsername: z.string().optional(),
  claimedAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime().nullable(),
  publicAddress: z.string().nullable(),
  publicAddressMatches: z.boolean(),
  httpsRequired: z.boolean(),
  relayEnabled: z.boolean(),
  capabilities: z.array(z.string()).default([]),
  presence: z.object({
    online: z.boolean(),
    lastSeenAt: z.iso.datetime().nullable(),
  }),
  // The broker's last outside-in verdict. Visible to every grant holder
  // (they already see the same hosts in connections); probedUri is
  // included for the owner only. Optional: pre-resolver payloads omit it.
  reachability: reachabilitySchema.optional(),
  connections: z.array(connectionCandidateSchema).optional(),
  serverToken: serverScopedTokenSchema.optional(),
});
export type ResourceItem = z.infer<typeof resourceItemSchema>;

export const resourcesResponseSchema = z.object({
  meta: metaSchema,
  items: z.array(resourceItemSchema),
});
export type ResourcesResponse = z.infer<typeof resourcesResponseSchema>;

export const mintedServerTokenSchema = z.object({
  token: z.string(),
  expiresAt: z.iso.datetime(),
  serverId: z.uuid(),
});
export type MintedServerToken = z.infer<typeof mintedServerTokenSchema>;
